/**
 * Authentication Utilities
 * Handles token management and user authentication state
 */

import { message } from 'antd';
import { STORAGE_KEYS } from '../constants';
import { logout } from '../api/services/authService';
import { clearAllCaches } from '../cache';
import i18next from 'i18next';

/**
 * Get the appropriate storage based on "Remember Me" preference
 * localStorage persists across sessions, sessionStorage clears on tab close
 */
const getStorage = () => {
  const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

/**
 * Get auth data from both storages
 */
const getAuthDataRaw = () => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_DATA) || sessionStorage.getItem(STORAGE_KEYS.AUTH_DATA);
};

/**
 * Set Remember Me preference (this always goes in localStorage)
 */
export const setRememberMe = (remember) => {
  if (remember) {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
  } else {
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
  }
};

/**
 * Get the current access token from storage
 */
export const getAccessToken = () => {
  const authData = getAuthDataRaw();
  if (!authData) return null;
  try {
    return JSON.parse(authData).accessToken;
  } catch {
    return null;
  }
};

/**
 * Get the current refresh token from storage
 */
export const getRefreshToken = () => {
  const authData = getAuthDataRaw();
  if (!authData) return null;
  try {
    return JSON.parse(authData).refreshToken;
  } catch {
    return null;
  }
};

/**
 * Check if the access token has expired
 */
export const isAccessTokenExpired = () => {
  const authData = getAuthDataRaw();
  if (!authData) return true;
  try {
    const { accessExpiration } = JSON.parse(authData);
    // Add a small buffer (5 seconds) to refresh slightly before expiration
    return new Date().getTime() >= (accessExpiration - 5000);
  } catch {
    return true;
  }
};

/**
 * Save authentication data to storage
 */
export const setAuthData = (data, rememberMe = null) => {
  // If rememberMe is explicitly passed, update the preference
  if (rememberMe !== null) {
    setRememberMe(rememberMe);
  }

  const authData = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    accessExpiration: data.accessExpiration,
    // Carried on the user so App can gate every route on it without a second
    // fetch. Advisory only — the server refuses the same requests regardless.
    user: { ...data.user, mustChangePassword: data.mustChangePassword === true },
  };

  const storage = getStorage();
  // Clear from both storages first to avoid duplicates
  localStorage.removeItem(STORAGE_KEYS.AUTH_DATA);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_DATA);

  storage.setItem(STORAGE_KEYS.AUTH_DATA, JSON.stringify(authData));
};

/**
 * Remove authentication data from all storage, and with it everything cached
 * on that user's behalf — the next person to sign in on this browser must
 * start cold.
 */
export const deleteAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_DATA);
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_DATA);
  localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
  clearAllCaches();
};

/**
 * Get the current user's credentials
 */
export const getUserCredentials = () => {
  const authData = getAuthDataRaw();
  if (!authData) return null;
  try {
    return JSON.parse(authData).user;
  } catch {
    return null;
  }
};

/**
 * Check if user can login (valid token exists)
 */
export const canUserLogin = () => {
  if (!isAccessTokenExpired()) {
    return true;
  }

  const token = getAccessToken();
  const refresh = getRefreshToken();
  if (token && isAccessTokenExpired() && refresh) {
    // Token refresh will be handled by the API interceptor
    return true;
  }

  if (!token && !refresh) {
    deleteAuthData();
    return false;
  }

  return false;
};

/**
 * Flag to indicate logout is in progress - suppresses error messages
 */
let isLoggingOut = false;

/**
 * Check if a logout is currently in progress
 */
export const isLogoutInProgress = () => isLoggingOut;

/**
 * Entries the service worker caches on the user's behalf — the one API path
 * it is allowed to cache (CACHEABLE_API_PATHS in public/service-worker.js).
 */
const USER_CONTENT_CACHE_PATH = '/download/public/';

/**
 * Drop the user's content from the service worker caches (called on logout).
 *
 * Only that: the app shell, offline page and static assets stay. Deleting the
 * whole cache used to leave the next offline visit with a bare "Offline" body
 * instead of the app, until a service worker update precached again.
 */
const clearServiceWorkerCaches = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(async (name) => {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        await Promise.all(
          requests
            .filter((request) => request.url.includes(USER_CONTENT_CACHE_PATH))
            .map((request) => cache.delete(request))
        );
      }));
      console.log('[Auth] User content cleared from service worker caches on logout');
    } catch (error) {
      console.warn('[Auth] Failed to clear SW caches:', error);
    }
  }
};

/**
 * Handle user logout
 */
export const handleLogout = async (navigate, options = {}) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  const { beforeLogout } = options;

  try {
    const token = getAccessToken();
    const refresh = getRefreshToken();
    if (!token) {
      deleteAuthData();
      await clearServiceWorkerCaches();
      navigate('/login');
      message.info(i18next.t('auth.sessionExpired'));
      return;
    }

    if (typeof beforeLogout === 'function') {
      try {
        // Never let optional cleanup (e.g. unsubscribing from push) block the
        // logout itself — cap it so a hung promise can't strand the user.
        await Promise.race([
          Promise.resolve(beforeLogout()),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (error) {
        console.warn('[Auth] Pre-logout cleanup failed:', error);
      }
    }

    // Delete auth data first to prevent interceptors from firing with stale tokens
    deleteAuthData();

    // Clear service worker caches
    await clearServiceWorkerCaches();

    // Fire-and-forget logout API call with the captured tokens so the server
    // can revoke both of them - don't wait or handle errors
    logout(token, refresh).catch(() => {});

    navigate('/login');
    message.success(i18next.t('auth.logoutSuccess'));
  } catch (error) {
    deleteAuthData();
    await clearServiceWorkerCaches();
    navigate('/login');
  } finally {
    // Reset flag after a short delay to allow any in-flight requests to be suppressed
    setTimeout(() => {
      isLoggingOut = false;
    }, 2000);
  }
};

